import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";

const EMPTY = { question: "", answer: "", category: "General", order: 99, status: "active" };
const CATS = ["General", "Personal Loan", "Home Loan", "Business Loan", "Education Loan", "Vehicle Loan", "LAP", "Eligibility", "Documents", "Application", "Credit Score"];

export default function FaqsAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get("/admin/faqs").then((r) => setRows(r.data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing?.id) await api.put(`/admin/faqs/${editing.id}`, { ...form, order: Number(form.order) });
      else await api.post("/admin/faqs", { ...form, order: Number(form.order) });
      toast.success("FAQ saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this FAQ?")) return;
    await api.delete(`/admin/faqs/${id}`);
    load();
  };

  return (
    <div data-testid="admin-faqs-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">FAQs</h1>
          <p className="text-sm text-brand-muted mt-0.5">Active FAQs appear on the website by category</p>
        </div>
        <button onClick={() => { setEditing({}); setForm(EMPTY); }} data-testid="add-faq-btn" className="btn-primary !px-5 !py-2.5 !text-xs"><Plus size={14} /> Add FAQ</button>
      </div>

      {editing && (
        <form onSubmit={save} className="bg-white rounded-xl border border-black/5 p-5 mb-6 grid sm:grid-cols-3 gap-4" data-testid="faq-form">
          <div className="sm:col-span-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Question *</label>
            <input required className="input-site mt-1" value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" />
          </div>
          <div className="sm:col-span-3">
            <label className="text-[10px] uppercase tracking-wider font-bold text-brand-muted">Answer *</label>
            <textarea required className="input-site mt-1 min-h-[90px]" value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer" />
          </div>
          <select className="input-site" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="faq-category">
            {CATS.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input type="number" className="input-site" placeholder="Order" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} />
          <select className="input-site" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">Active</option><option value="inactive">Inactive</option>
          </select>
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" data-testid="faq-save-btn" className="btn-primary !px-5 !py-2.5 !text-xs">Save FAQ</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline !px-4 !py-2.5 !text-xs"><X size={13} /></button>
          </div>
        </form>
      )}

      {!rows ? <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div> : (
        <div className="space-y-3">
          {rows.map((f) => (
            <div key={f.id} className="bg-white rounded-xl border border-black/5 p-5 flex items-start justify-between gap-4" data-testid={`faq-row-${f.id}`}>
              <div>
                <p className="font-semibold text-brand-dark text-sm">{f.question}</p>
                <p className="text-xs text-brand-muted mt-1 line-clamp-2">{f.answer}</p>
                <p className="text-[10px] text-brand-gold font-bold uppercase tracking-wider mt-2">{f.category} · order {f.order} · {f.status}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                <button onClick={() => { setEditing(f); setForm(f); }} data-testid={`faq-edit-${f.id}`} className="text-brand-muted hover:text-brand-dark"><Pencil size={15} /></button>
                <button onClick={() => remove(f.id)} data-testid={`faq-delete-${f.id}`} className="text-brand-muted hover:text-brand-red"><Trash2 size={15} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
